class ExtraServices extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hidden: 'hidden',
      active: 'active',
      open: 'open',
      serviceHeader: '.extra-service__header',
      serviceCheckboxes: '.js-extra-service-checkbox',
      spinner: '.loading-overlay__spinner',
    };

    this.contentContainer = this.querySelector('.extra-services__content');
    this.notificationContainer = document.querySelector('.extra-services__notification');
    this.serviceCheckboxes = Array.from(this.querySelectorAll(this.classes.serviceCheckboxes));
    this.handlingFeeVariant =
      this.querySelector(`#cart-drawer-extra-service-${window.extraServices.low_order_value_service_fee_product}`)
        ?.dataset?.variantId || null;
    this.handlingFeeMinimum = window.extraServices.low_order_value * 100 || 1500;
    this.extendedReturnsVariant =
      this.querySelector(`#cart-drawer-extra-service-${window.extraServices.extended_returns_product}`)?.dataset
        ?.variantId || null;
    this.isSyncInProgress = false;
    this.isUpdatingFromSelf = false;
    this.isReadOnlySyncInProgress = false;
    this.pendingOperations = new Map();
    this.processingVariants = new Set();
    this.cachedSections = null;

    this.serviceCheckboxes.forEach(
      (checkbox) =>
        checkbox.dataset.serviceType !== 'mandatory' &&
        checkbox.addEventListener('change', this.onServiceCheckboxToggle.bind(this)),
    );
  }

  connectedCallback() {
    // Evaluate the cart on page load.
    this.fetchCart()
      .then((cart) => this.processCartContents(cart))
      .catch((error) => console.error('Extra services load-time sync failed:', error));
  }

  async fetchCart() {
    return fetch(`${window.Shopify.routes.root}cart.js`).then(async (response) => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    });
  }

  async updateCartItem(variantId, quantity) {
    if (this.isVariantReplacementInProgress) {
      return;
    }

    const section = this.getSectionsToRender().map((section) => section.section);
    const data = { sections: section };

    const isAdd = quantity > 0;
    // Services must use the idempotent cart/update.js for adds: it sets the
    // ABSOLUTE quantity, so racing/duplicate operations can never stack a
    // service variant to qty 2+.
    // Removals keep the idempotent cart/change.js qty 0.
    const endpoint = isAdd ? 'cart/update.js' : 'cart/change.js';
    const payload = isAdd
      ? { updates: { [variantId]: quantity }, sections: data.sections }
      : { id: variantId, quantity: 0, sections: data.sections };

    const checkbox = document.querySelector(`input[data-variant-id="${variantId}"]`);
    const spinner = checkbox?.closest(this.classes.serviceHeader)?.querySelector(this.classes.spinner);

    return fetch(`${window.Shopify.routes.root}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Cart API error (${response.status}):`, errorText);
          throw new Error(`Cart API failed: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const responseJson = JSON.parse(await response.text());

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: isAdd ? 'cart-items-add' : 'cart-items',
          cartData: responseJson,
        });
        return responseJson;
      })
      .then((parsedResponse) => {
        spinner?.classList.add(this.classes.hidden);
        this.renderContents(parsedResponse);
        // sync checkbox states from cart response instead of relying on DOM re-render
        if (parsedResponse.items) {
          this.syncServiceCheckboxes(parsedResponse.items);
          this.syncManipulationFeeVisibility(parsedResponse.items);
        }
        return parsedResponse;
      });
  }

  async addItemToCart(variantId, isAutomaticUpdate = false) {
    try {
      const cart = await this.fetchCart();
      const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
      if (cartVariantIds.includes(variantId)) {
        return cart;
      }
    } catch (error) {
      const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
      if (cartItemsVariantIds.includes(variantId)) {
        console.error('Variant found in cart:', variantId);
        return;
      }
    }

    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (isAutomaticUpdate && !wasAlreadyUpdating) {
      this.isUpdatingFromSelf = true;
    }

    return this.updateCartItem(variantId, 1).finally(() => {
      if (isAutomaticUpdate && !wasAlreadyUpdating) {
        this.isUpdatingFromSelf = false;
      }
    });
  }

  async removeItemFromCart(variantId, isAutomaticUpdate = false) {
    try {
      const cart = await this.fetchCart();
      const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
      if (!cartVariantIds.includes(variantId)) {
        return cart;
      }
    } catch (error) {
      const cartItemsVariantIds = this.dataset.cartItemsVariantIds.split(',');
      if (!cartItemsVariantIds.includes(variantId)) {
        console.error('Variant not found in cart:', variantId);
        return;
      }
    }

    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (isAutomaticUpdate && !wasAlreadyUpdating) {
      this.isUpdatingFromSelf = true;
    }

    return this.updateCartItem(variantId, 0).finally(() => {
      if (isAutomaticUpdate && !wasAlreadyUpdating) {
        this.isUpdatingFromSelf = false;
      }
    });
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const elementToReplace = sectionElement?.querySelector(section.selector) || sectionElement;
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.applySections === 'function') {
      cartDrawer.applySections(parsedState, true);
    }

    this.cachedSections = null;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    if (this.cachedSections) {
      return this.cachedSections;
    }

    const cartDrawerElement = document.getElementById('CartDrawer');
    const mainCartElement = document.getElementById('main-cart');

    const sections = [
      {
        id: 'CartDrawer',
        section: cartDrawerElement?.dataset.id,
        selector: '.drawer__contents.js-contents',
        element: cartDrawerElement,
      },
      {
        id: 'main-cart',
        section: mainCartElement?.dataset.id,
        selector: '.js-contents',
        element: mainCartElement,
      },
    ];

    this.cachedSections = sections.filter((s) => s.section != null);
    return this.cachedSections;
  }

  onServiceCheckboxToggle(event) {
    const spinner = event.target?.closest(this.classes.serviceHeader)?.querySelector(this.classes.spinner);
    spinner?.classList.remove(this.classes.hidden);
    const checkbox = event.target;
    const variantId = checkbox.dataset.variantId;

    const targetState = checkbox.checked;
    const originalState = !targetState;
    checkbox.disabled = true;

    this.isUpdatingFromSelf = true;

    const operation = targetState ? this.addItemToCart(variantId) : this.removeItemFromCart(variantId);

    const promise = operation
      .then(async (cartData) => {
        const notifyToggleResult = () => {
          this.showNotification(
            targetState ? 'service-added' : 'service-removed',
            targetState ? 'success' : 'warning',
            checkbox.dataset.serviceTitle,
          );
        };

        const hasFullCart = cartData?.token && Array.isArray(cartData?.items);
        if (hasFullCart) {
          // Notify after re-render, otherwise the section update wipes the notification
          return Promise.resolve(this.processCartContents(cartData)).then(notifyToggleResult);
        }
        const cart = await this.fetchCart();

        const cartVariantIds = cart.items.map((i) => i.variant_id.toString());
        const isInCart = cartVariantIds.includes(variantId);

        if (targetState && !isInCart) {
          console.error('Service add operation failed - item not in cart');
          checkbox.checked = originalState;
          return this.processCartContents(cart);
        }
        if (!targetState && isInCart) {
          console.error('Service remove operation failed - item still in cart');
          checkbox.checked = originalState;
          return this.processCartContents(cart);
        }
        return Promise.resolve(this.processCartContents(cart)).then(notifyToggleResult);
      })
      .then(() => {
        this.isUpdatingFromSelf = false;
      })
      .catch((error) => {
        console.error('Service toggle failed:', error);
        checkbox.checked = originalState;
        this.isUpdatingFromSelf = false;
      })
      .finally(() => {
        checkbox.disabled = false;
        this.pendingOperations.delete(variantId);
        if (this.pendingOperations.size === 0) {
          this.isUpdatingFromSelf = false;
        }
      });

    this.pendingOperations.set(variantId, promise);
    return promise;
  }

  updateServices() {
    if (this.isSyncInProgress) return;

    if (this.isVariantReplacementInProgress) {
      clearVariantReplacementFlagAfterSkip();
      return;
    }

    if (this.pendingOperations.size > 0 && !this.isUpdatingFromSelf) {
      return Promise.all(this.pendingOperations.values()).then(() => {
        return this.fetchCart().then((cart) => this.processCartContents(cart));
      });
    }

    this.isSyncInProgress = true;
    return this.fetchCart()
      .then((cart) => this.processCartContents(cart))
      .catch((error) => {
        console.error('Cart sync error:', error);
      })
      .finally(() => (this.isSyncInProgress = false));
  }

  processCartContents(cart) {
    const serviceIds = this.serviceCheckboxes.map((cb) => cb.dataset.variantId);
    const cartItems = cart.items || [];
    const nonServiceItems = cartItems.filter((item) => !isServiceLineItem(item, serviceIds));
    const cartTotal = nonServiceItems.reduce((sum, item) => sum + (item.final_line_price || item.line_price), 0);

    if (cartItems.length === 0) {
      this.syncServiceCheckboxes([]);
      this.syncManipulationFeeVisibility([]);
      return Promise.resolve();
    }

    // Only collapse a cart that still HAS items: nonServiceItems is empty for an
    // already-empty cart too, and clearing that again costs a round-trip plus a
    // section re-render that can wipe a just-shown notification.
    if (nonServiceItems.length === 0) {
      const cartElement = document.querySelector('cart-items, cart-drawer-items');
      if (cartElement && typeof cartElement.cartClear === 'function') {
        cartElement.cartClear();
        return Promise.resolve();
      }

      const removalPromises = this.serviceCheckboxes
        .filter((cb) => cb.checked)
        .map((cb) => {
          cb.checked = false;
          return this.removeItemFromCart(cb.dataset.variantId).then(() => {
            this.showNotification('service-removed', 'warning', cb.dataset.serviceTitle);
          });
        });

      // NB: COD / checkout-added fees are NOT handled here. This branch is only
      // reachable when no <cart-items>/<cart-drawer-items> exists, and module-scoped
      // cleanupOrphanedServices() already owns that case for every line-item type.
      return Promise.all(removalPromises);
    }

    this.healServiceQuantities(cart.items);
    this.syncServiceCheckboxes(cart.items);
    this.syncManipulationFeeVisibility(cart.items);
    this.updateHandlingFee(cartTotal, cart.items);
    this.addExtendedReturnsIfNeeded(cart.items);
  }

  /**
   * Invariant: a service variant is always at qty 0 or 1. If a desync ever
   * pushes one above 1 (e.g. the hidden line-item stepper, or a historical
   * add race), reset it to exactly 1 via the idempotent update path.
   */
  healServiceQuantities(cartItems) {
    this.serviceCheckboxes.forEach((cb) => {
      const variantId = cb.dataset.variantId;
      const matchingItems = cartItems.filter((i) => i.variant_id.toString() === variantId);

      const totalQty = matchingItems.reduce((sum, i) => sum + i.quantity, 0);
      if (matchingItems.length === 0 || (matchingItems.length === 1 && totalQty <= 1)) return;

      if (this.processingVariants.has(variantId)) return;

      this.processingVariants.add(variantId);
      const wasAlreadyUpdating = this.isUpdatingFromSelf;
      if (!wasAlreadyUpdating) this.isUpdatingFromSelf = true;

      const updates = {};
      matchingItems.forEach((item, index) => {
        updates[item.key] = index === 0 ? 1 : 0;
      });

      const section = this.getSectionsToRender().map((s) => s.section);
      const payload = { updates, sections: section };

      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(async (response) => {
          if (!response.ok) throw new Error(response.statusText);
          const responseJson = await response.json();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: responseJson });
          this.renderContents(responseJson);
          if (responseJson.items) this.syncServiceCheckboxes(responseJson.items);
        })
        .catch((error) => {
          console.error('Failed to heal service quantity:', error);
        })
        .finally(() => {
          this.processingVariants.delete(variantId);
          if (!wasAlreadyUpdating) this.isUpdatingFromSelf = false;
        });
    });
  }

  addExtendedReturnsIfNeeded(cartItems) {
    if (!this.extendedReturnsVariant) return;

    const variantStr = this.extendedReturnsVariant.toString();
    if (this.processingVariants.has(variantStr)) return;
    if (cartItems.some((i) => i.variant_id.toString() === variantStr)) return;

    const checkbox = this.querySelector(`input[data-variant-id="${variantStr}"]`);
    this.processingVariants.add(variantStr);
    const wasAlreadyUpdating = this.isUpdatingFromSelf;
    if (!wasAlreadyUpdating) this.isUpdatingFromSelf = true;
    if (checkbox) checkbox.checked = true;

    this.addItemToCart(this.extendedReturnsVariant, true).finally(() => {
      this.processingVariants.delete(variantStr);
      if (!wasAlreadyUpdating) this.isUpdatingFromSelf = false;
    });
  }

  updateHandlingFee(cartTotal, cartItems) {
    const handlingFeeCheckbox = this.querySelector(`input[data-variant-id="${this.handlingFeeVariant}"]`);
    if (!handlingFeeCheckbox) return;

    const handlingFeeVariantStr = this.handlingFeeVariant.toString();

    if (this.processingVariants.has(handlingFeeVariantStr)) {
      return;
    }

    const handlingFeePresent = cartItems.some((i) => i.variant_id.toString() === handlingFeeVariantStr);
    const shouldAddHandlingFee = cartTotal < this.handlingFeeMinimum && !handlingFeePresent;
    const shouldRemoveHandlingFee = cartTotal >= this.handlingFeeMinimum && handlingFeePresent;

    if (shouldAddHandlingFee) {
      this.processingVariants.add(handlingFeeVariantStr);
      const wasAlreadyUpdating = this.isUpdatingFromSelf;
      if (!wasAlreadyUpdating) {
        this.isUpdatingFromSelf = true;
      }
      handlingFeeCheckbox.checked = true;
      this.addItemToCart(this.handlingFeeVariant, true)
        .then(() => {
          this.showNotification('service-added', 'warning', handlingFeeCheckbox.dataset.serviceTitle);
        })
        .catch((error) => {
          console.error('Failed to add handling fee:', error);
        })
        .finally(() => {
          this.processingVariants.delete(handlingFeeVariantStr);
          if (!wasAlreadyUpdating) {
            this.isUpdatingFromSelf = false;
          }
        });
    } else if (shouldRemoveHandlingFee) {
      this.processingVariants.add(handlingFeeVariantStr);
      const wasAlreadyUpdating = this.isUpdatingFromSelf;
      if (!wasAlreadyUpdating) {
        this.isUpdatingFromSelf = true;
      }
      handlingFeeCheckbox.checked = false;
      this.removeItemFromCart(this.handlingFeeVariant, true)
        .then(() => {
          this.showNotification('service-removed', 'success', handlingFeeCheckbox.dataset.serviceTitle);
        })
        .catch((error) => {
          console.error('Failed to remove handling fee:', error);
        })
        .finally(() => {
          this.processingVariants.delete(handlingFeeVariantStr);
          if (!wasAlreadyUpdating) {
            this.isUpdatingFromSelf = false;
          }
        });
    }
  }

  syncManipulationFeeVisibility(cartItems) {
    const feeVariant = this.handlingFeeVariant?.toString();
    if (!feeVariant) return;

    const feePresent = (cartItems || []).some((item) => item.variant_id.toString() === feeVariant);

    document.querySelectorAll('.manipulation-fee-block').forEach((block) => {
      block.classList.toggle(this.classes.hidden, !feePresent);
    });
  }

  syncServiceCheckboxes(cartItems) {
    const cartVariantIds = cartItems.map((i) => i.variant_id.toString());
    this.serviceCheckboxes.forEach((cb) => {
      const shouldBeChecked = cartVariantIds.includes(cb.dataset.variantId);
      if (cb.checked !== shouldBeChecked) {
        cb.checked = shouldBeChecked;
      }
    });
  }

  /**
   * Show a service notification next to the extra-services block.
   * Clones the prerendered Liquid template (cart-notification-templates.liquid)
   * via the global showCartNotification helper (global.js).
   * @param {string} templateName - e.g. 'service-added' | 'service-removed'
   * @param {string} type - Severity override: 'success' | 'warning' | 'alert'
   * @param {string} serviceTitle - The service product title
   */
  showNotification(templateName, type = 'success', serviceTitle) {
    if (!templateName || !serviceTitle) return;
    if (typeof window.showCartNotification !== 'function') return;

    // Resolve the container at call time: cart/drawer section re-renders replace
    // the DOM, so a reference cached at construction may be detached. Prefer a
    // container inside this instance (drawer vs cart page may both be present).
    const container = this.isConnected
      ? this.querySelector('.extra-services__notification') || document.querySelector('.extra-services__notification')
      : document.querySelector('.extra-services__notification');

    window.showCartNotification(templateName, { title: serviceTitle, value: 1 }, { container, type, duration: 3000 });
  }
}

customElements.define('extra-services', ExtraServices);

function clearVariantReplacementFlagAfterSkip() {
  queueMicrotask(() => {
    document.querySelectorAll('extra-services').forEach((el) => {
      el.isVariantReplacementInProgress = false;
    });
  });
}

function debounceExtraServices(fn, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Global fallback to clear stranded service items (including COD fees).
 *
 * Runs on page load and when the cart updates but the <extra-services>
 * component is missing (e.g. when the cart empties and the empty-cart
 * template renders). Without this, COD/Service items would remain hidden
 * in the cart and silently charge the customer on their next order.
 */
let orphanedServicesCleanupInFlight = false;

/**
 * Vendor whose line items are checkout-added service fees (COD, bank transfer).
 * @returns {string}
 */
function getCodProductVendor() {
  return window.shopSettings?.cod_product_vendor || 'Additional service';
}

/**
 * True when a cart line item is a service rather than something the customer
 * chose to buy: either a metaobject-driven extra service (matched by variant
 * id) or a checkout-added fee such as COD (matched by vendor).
 * @param {object} item - a line item from cart.js
 * @param {string[]} serviceVariantIds - window.extraServices.service_variant_ids
 * @returns {boolean}
 */
function isServiceLineItem(item, serviceVariantIds) {
  if (serviceVariantIds.includes(item.variant_id.toString())) return true;
  const codVendor = getCodProductVendor();
  return !!codVendor && item.vendor === codVendor;
}

async function cleanupOrphanedServices() {
  if (orphanedServicesCleanupInFlight) return;
  // NOTE: an empty service_variant_ids list is still actionable — the COD
  // vendor match below does not depend on it.
  const serviceVariantIds = window.extraServices?.service_variant_ids;
  if (!Array.isArray(serviceVariantIds)) return;

  orphanedServicesCleanupInFlight = true;

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error(response.statusText);

    const cart = await response.json();
    if (!cart.items.length) return;

    // Check if every item in the cart is a service fee or COD
    const hasOnlyServices = cart.items.every((item) => isServiceLineItem(item, serviceVariantIds));

    if (hasOnlyServices) {
      const cartElement = document.querySelector('cart-items, cart-drawer-items');

      // If the cart UI is on screen, tell it to clear itself so the screen updates
      if (cartElement && typeof cartElement.cartClear === 'function') {
        cartElement.cartClear();
      } else {
        // Otherwise, simply clear the entire cart in the background
        await fetch(`${window.Shopify.routes.root}cart/clear.js`, { method: 'POST' });
      }
    }
  } catch (error) {
    console.error('Orphaned services cleanup failed:', error);
  } finally {
    orphanedServicesCleanupInFlight = false;
  }
}

const debouncedCartUpdate = debounceExtraServices((event) => {
  const extraServicesElements = document.querySelectorAll('extra-services');
  if (extraServicesElements.length === 0) {
    // Element gone (e.g. empty-cart re-render) — still enforce the
    // "no services-only cart" invariant.
    cleanupOrphanedServices();
    return;
  }

  const isAnyUpdating = Array.from(extraServicesElements).some(
    (el) => el.isUpdatingFromSelf || el.pendingOperations.size > 0 || el.isSyncInProgress,
  );

  if (isAnyUpdating) {
    // Busy: retry once current operations settle instead of dropping the
    // update — a dropped final event left the fee state permanently stale.
    debouncedCartUpdate(event);
    return;
  }

  const handlingFeeVariant = extraServicesElements[0]?.handlingFeeVariant?.toString();
  if (handlingFeeVariant) {
    const isProcessingFee = Array.from(extraServicesElements).some((el) =>
      el.processingVariants?.has(handlingFeeVariant),
    );
    if (isProcessingFee) {
      debouncedCartUpdate(event); // same: retry, don't drop
      return;
    }
  }

  if (extraServicesElements.length > 0) {
    extraServicesElements[0].updateServices();
  }
}, 50);

subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  const extraServicesElements = document.querySelectorAll('extra-services');
  if (extraServicesElements.length > 0) {
    const isAnyUpdating = Array.from(extraServicesElements).some(
      (el) => el.isUpdatingFromSelf || el.pendingOperations.size > 0 || el.isSyncInProgress,
    );

    if (isAnyUpdating) {
      // Busy: hand off to the debounced retry instead of dropping the update
      debouncedCartUpdate(event);
      return;
    }
  }

  debouncedCartUpdate(event);
});

// Heal carts stranded in a services-only state by a previous page/session
// (the empty-cart template renders no <extra-services> element, so nothing
// else would ever clean them up).
cleanupOrphanedServices();
